/**
 * The STRICTLY valid readings of a `key=value` connection string (Option C C3 review r5, C3-21): each keyword format
 * reads the text exactly as its own parser does and counts only when that parser would accept it —
 *
 * - libpq: `conninfo_parse` accepts it and every keyword is libpq's (matched exactly);
 * - ADO.NET (SqlClient rules) and ODBC: .NET's `DbConnectionOptions` accepts it and every keyword is SqlClient's / the ODBC
 *   driver's; a SQL Server address is never a URL or a Kusto host;
 * - Npgsql and MySqlConnector (ruling after r5): the same .NET parser accepts it, every keyword is that provider's and its
 *   SSL Mode value one the provider parses; a host is never a URL or a Kusto host;
 * - Kusto: the installed SDK's builder accepts it (no unknown keyword, no empty item) and its Data Source is a URL or a
 *   Kusto host;
 * - Azure Storage: every keyword is a storage keyword;
 *
 * — and its mapped result puts no `;` / `=` property text into a field other than the secret (C3-14). A credential whose
 * value is entirely a portal placeholder (`{your_password}` …, `placeholders.ts`) is not given: it is left out before the
 * reading is mapped, so it neither fills a field, nor counts as a secret, nor refuses the string. Each reading carries
 * its secret values as that format reads them, so the orchestrator can refuse readings that disagree about a secret's
 * boundaries (rule 5) and let the form's engine choose only among valid, agreeing ones (rule 6).
 */
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import { KUSTO_KEYWORDS, kustoBuilderProperties } from '@/core/engines/kustoConnectionString';
import { AZURE_STORAGE_KEYS } from '@/core/engines/azureStorageConnectionString';
import { PLAIN_SEMICOLON_SYNTAX, scanDotNet, scanFailed, scanKeyValues, scanLibpq, type KeyValuePair, type QuoteMark, type Scan } from '@/core/utils/connectionStringTokens';
import type { ParserMatch, ReadingFormat } from './types';
import {
  AZURE_STORAGE_DOCUMENTED_KEYS, AZURE_STORAGE_SECRET_KEYS, CREDENTIAL_KEYS, LIBPQ_KEYWORDS, MYSQLCONNECTOR_KEYWORDS, NPGSQL_KEYWORDS, ODBC_KEYWORDS,
  SQLCLIENT_KEYWORDS,
} from './connectionStringKeywords';
import { adoNetMatch } from './parseAdoNet';
import { mysqlConnectorAccepts, mysqlConnectorMatch, npgsqlAccepts, npgsqlMatch } from './parseDotNetProviders';
import { credentialsIn, isPlaceholder, withoutPlaceholders } from './placeholders';
import { MSSQL_KEYWORD_RULES, type DuplicateKeyRule } from './repeatedKeywords';
import { libpqMatch } from './parseLibpq';
import { parseKusto } from './parseKusto';
import { parseAzureStorage } from './parseAzureStorage';
import { isKustoLikeAddress } from './kustoAddress';

export type Reading = { format: ReadingFormat; match: ParserMatch; secrets: string[] };

export const ENGINE_OF_READING: Readonly<Record<ReadingFormat, DatabaseEngine>> = {
  libpq: 'pgsql',
  adonet: 'mssql',
  odbc: 'mssql',
  npgsql: 'pgsql',
  mysqlconnector: 'mysql',
  kusto: 'kusto',
  'azure-storage': 'azurestorage',
};

/**
 * The effective secret values, in key order: one per key (`groupOf` folds a format's synonyms into one), the occurrence
 * the format's driver uses — the last, or with ODBC the first (`repeatedKeywords.ts`).
 */
function secretsOf(
  pairs: readonly KeyValuePair[],
  isSecret: (key: string) => boolean,
  groupOf: (key: string) => string = (key) => key.toLowerCase(),
  rule: DuplicateKeyRule = 'last'
): string[] {
  const byKey = new Map<string, string>();
  for (const { key, value } of pairs) {
    const group = groupOf(key);
    if (isSecret(key) && (rule === 'last' || !byKey.has(group))) byKey.set(group, value);
  }
  return [...byKey.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, value]) => value);
}

/** A result that would put `;` / `=` into a field other than the secret (SQLite file paths are paths, not properties). */
export function holdsPropertyText(match: ParserMatch): boolean {
  if (match.format === 'sqlite_path' || match.problem) return false;
  return Object.entries(match.values).some(([field, value]) => field !== 'password' && typeof value === 'string' && /[;=]/u.test(value));
}

/** A reading that exists but opens a value with a quote its format does not honour (the r5 ruling): refused, not used. */
type Found = Omit<Reading, 'format'> | { opening: QuoteMark } | null;

const openingIn = (pairs: readonly KeyValuePair[]): { opening: QuoteMark } | null => {
  const opened = pairs.find((pair) => pair.opening);
  return opened?.opening ? { opening: opened.opening } : null;
};

function libpqReading(scan: Scan): Found {
  const pairs = scanFailed(scan) ? null : scan;
  if (!pairs || pairs.length === 0 || !pairs.every((pair) => LIBPQ_KEYWORDS.has(pair.key))) return null;
  const credentials = CREDENTIAL_KEYS.libpq;
  const kept = withoutPlaceholders(pairs, credentialsIn(credentials));
  const opened = openingIn(kept);
  if (opened) return opened;
  const match = libpqMatch(kept) ?? onlyPlaceholders('libpq', 'pgsql', pairs, kept);
  return match ? { match, secrets: secretsOf(kept, (key) => credentials.secrets.has(key)) } : null;
}

/**
 * A string whose recognised credentials were all placeholders (`Password={your_password}`): read, filling nothing — the
 * credential is still needed — rather than "not recognised".
 */
function onlyPlaceholders(format: ParserMatch['format'], engine: ParserMatch['engine'], pairs: readonly KeyValuePair[], kept: readonly KeyValuePair[]): ParserMatch | null {
  if (kept.length === pairs.length) return null;
  return { format, engine, values: {}, matchedFields: [], unmatchedKeys: kept.map((pair) => pair.key) };
}

/** A keyword format read by .NET's `DbConnectionOptions`: its keywords, credential keys and mapping. */
type DotNetFormat = {
  keywords: ReadonlySet<string>;
  credentials: (typeof CREDENTIAL_KEYS)[keyof typeof CREDENTIAL_KEYS];
  match: (pairs: readonly KeyValuePair[]) => ParserMatch | null;
  engine: DatabaseEngine;
  /** Value checks the provider's builder makes (Npgsql / MySqlConnector: the SSL Mode names they parse). */
  accepts?: (pairs: readonly KeyValuePair[]) => boolean;
  /** Which occurrence of a repeated keyword the driver uses (`repeatedKeywords.ts`). */
  repeated: DuplicateKeyRule;
};

const DOT_NET_FORMATS: Readonly<Record<'adonet' | 'odbc' | 'npgsql' | 'mysqlconnector', DotNetFormat>> = {
  adonet: {
    keywords: SQLCLIENT_KEYWORDS, credentials: CREDENTIAL_KEYS.adonet, match: (pairs) => adoNetMatch(pairs, 'sqlclient'), engine: 'mssql',
    repeated: MSSQL_KEYWORD_RULES.sqlclient.rule,
  },
  odbc: {
    keywords: ODBC_KEYWORDS, credentials: CREDENTIAL_KEYS.odbc, match: (pairs) => adoNetMatch(pairs, 'odbc'), engine: 'mssql',
    repeated: MSSQL_KEYWORD_RULES.odbc.rule,
  },
  // Npgsql's and MySqlConnector's builders parse with .NET's `DbConnectionOptions`, as SqlClient does ("last one wins");
  // their mappers let a later keyword or synonym override.
  npgsql: { keywords: NPGSQL_KEYWORDS, credentials: CREDENTIAL_KEYS.npgsql, match: npgsqlMatch, engine: 'pgsql', accepts: npgsqlAccepts, repeated: 'last' },
  mysqlconnector: {
    keywords: MYSQLCONNECTOR_KEYWORDS, credentials: CREDENTIAL_KEYS.mysqlconnector, match: mysqlConnectorMatch, engine: 'mysql', accepts: mysqlConnectorAccepts,
    repeated: 'last',
  },
};

function dotNetReading({ keywords, credentials, match: mapped, engine, accepts, repeated }: DotNetFormat, scan: Scan): Found {
  const pairs = scanFailed(scan) ? null : scan;
  if (!pairs || pairs.length === 0 || !pairs.every((pair) => keywords.has(pair.key.toLowerCase()))) return null;
  if (accepts && !accepts(pairs)) return null;
  const credentialOf = credentialsIn(credentials);
  const kept = withoutPlaceholders(pairs, credentialOf, repeated);
  const opened = openingIn(kept);
  if (opened) return opened;
  const match = mapped(kept) ?? onlyPlaceholders('adonet', engine, pairs, kept);
  if (!match || (typeof match.values.server === 'string' && isKustoLikeAddress(match.values.server))) return null;
  const secret = (key: string) => credentials.secrets.has(key.toLowerCase());
  return { match, secrets: secretsOf(kept, secret, (key) => credentialOf(key) ?? key.toLowerCase(), repeated) };
}

function kustoReading(text: string): Found {
  const properties = kustoBuilderProperties(text);
  if (!properties || typeof properties.dataSource !== 'string' || !isKustoLikeAddress(properties.dataSource)) return null;
  const match = parseKusto(text);
  if (!match) return null;
  const secrets = Object.keys(KUSTO_KEYWORDS)
    .filter((property) => KUSTO_KEYWORDS[property].isSecret && typeof properties[property] === 'string' && !isPlaceholder('password', String(properties[property])))
    .sort()
    .map((property) => String(properties[property]));
  return { match, secrets };
}

function storageReading(text: string): Found {
  const pairs = scanKeyValues(text, PLAIN_SEMICOLON_SYNTAX).segments.filter((segment): segment is KeyValuePair => segment !== null);
  if (pairs.length === 0 || !pairs.every((pair) => AZURE_STORAGE_DOCUMENTED_KEYS.has(pair.key.toLowerCase()))) return null;
  const opened = openingIn(pairs);
  if (opened) return opened;
  const match = parseAzureStorage(text);
  if (!match) return null;
  // Documented keywords the form has no field for are listed as not used, never silently dropped.
  const unused = pairs.filter((pair) => !AZURE_STORAGE_KEYS.has(pair.key.toLowerCase())).map((pair) => pair.key);
  const listed = unused.length > 0 && Object.keys(match.values).length > 0 ? { ...match, unmatchedKeys: [...match.unmatchedKeys, ...unused] } : match;
  return { match: listed, secrets: secretsOf(pairs, (key) => AZURE_STORAGE_SECRET_KEYS.has(key.toLowerCase())) };
}

export type StrictReadings = {
  readings: Reading[];
  /** Valid readings refused only because a non-secret field would hold property text — the reason a refusal can give. */
  propertyText: Reading[];
  /**
   * Formats whose own parser rejected the text because a quoted / braced value never closes: the author wrote that
   * format's quoting, so no other reading of the text is trusted (the string is refused).
   */
  quotingBroken: ReadingFormat[];
  /** Readings refused because a value opens with a quote their format does not honour, with that character. */
  unhonouredQuote: Array<{ format: ReadingFormat; quote: QuoteMark }>;
};

export function strictReadings(text: string): StrictReadings {
  const scans: Array<[ReadingFormat, Scan]> = [['libpq', scanLibpq(text)], ['adonet', scanDotNet(text, 'sqlclient')], ['odbc', scanDotNet(text, 'odbc')]];
  const quotingBroken = scans.filter(([, scan]) => scanFailed(scan) && scan.failure === 'quote').map(([format]) => format);
  // Npgsql's and MySqlConnector's builders parse with the same (non-ODBC) rules as SqlClient.
  const dotNet = scans[1][1];
  const found: Array<[ReadingFormat, Found]> = [
    ['libpq', libpqReading(scans[0][1])],
    ['adonet', dotNetReading(DOT_NET_FORMATS.adonet, dotNet)],
    ['odbc', dotNetReading(DOT_NET_FORMATS.odbc, scans[2][1])],
    ['npgsql', dotNetReading(DOT_NET_FORMATS.npgsql, dotNet)],
    ['mysqlconnector', dotNetReading(DOT_NET_FORMATS.mysqlconnector, dotNet)],
    ['kusto', kustoReading(text)],
    ['azure-storage', storageReading(text)],
  ];
  const readings: Reading[] = [];
  const propertyText: Reading[] = [];
  const unhonouredQuote: StrictReadings['unhonouredQuote'] = [];
  for (const [format, reading] of found) {
    if (!reading) continue;
    if ('opening' in reading) unhonouredQuote.push({ format, quote: reading.opening });
    else (holdsPropertyText(reading.match) ? propertyText : readings).push({ format, ...reading });
  }
  return { readings, propertyText, quotingBroken, unhonouredQuote };
}
